import { GameOption } from "entities/game/types";
import { Game } from "entities/game/types";
import {
  TEXT_BLOCK_DESCRIPTION_LIMIT,
  THREE_CARD_BLOCK_DESCRIPTION_LIMIT,
} from "entities/modules/constants";
import {
  CardBlockImage,
  ContentBlocksModuleLayout,
  ModuleContentBlocksContent,
  ModuleGame,
  ModuleLayout,
  ModuleTopPrivacyTipsBlockContent,
  ModuleType,
} from "entities/modules/types";
import { BrandType } from "../brands/types";

export const THREE_CARD_BLOCK_KEY = "three-card-block";
export const ONE_CARD_BLOCK_KEY = "one-card-block";

export const getImageKey = (key: string, index: number) => {
  return `${key}-${index}-image`;
};

export const defaultThreeCardBlock = () => {
  return new Array(3).fill({
    title: "",
    description: "",
    backgroundImage: null,
  });
};

export const defaultTextBlock = () => {
  return [
    {
      title: "",
      description: "",
    },
  ];
};

export const defaultOneCardBlock = () => {
  return [
    {
      title: "",
      description: "",
      backgroundImage: null,
      ctaForRegisteredUser: "",
      ctaForUnRegisteredUser: "",
      selectedGame: { value: "", label: "" },
      ctaDestination: "",
      url: "",
      urlOpensInNewTab: false,
    },
  ];
};

export const defaultTopPrivacyTipsBlock = () => {
  return {
    title: "",
    description: "",
    backgroundImage: null,
    ctaForRegisteredUser: "",
    ctaForUnRegisteredUser: "",
  };
};

export const validateTopPrivacyTipsBlock = (
  moduleType: ModuleType | null,
  cardBlockImage: CardBlockImage | null,
  contentBlock: ModuleTopPrivacyTipsBlockContent,
): boolean => {
  if (moduleType !== ModuleType.TopPrivacyTips) return true;

  if (!contentBlock || !cardBlockImage) return false;

  return (
    contentBlock.description.length <= TEXT_BLOCK_DESCRIPTION_LIMIT &&
    contentBlock.description.length > 0 &&
    contentBlock.title.length > 0 &&
    contentBlock.ctaForRegisteredUser.length > 0 &&
    contentBlock.ctaForUnRegisteredUser.length > 0 &&
    Boolean(cardBlockImage)
  );
};

export const validateContentBlocks = (
  moduleType: ModuleType | null,
  moduleLayout: ModuleLayout | null,
  cardBlockImages: CardBlockImage[],
  contentBlocks?: ModuleContentBlocksContent[],
  brandId?: string,
): boolean => {
  if (moduleType !== ModuleType.ContentBlocks) return true;

  if (!contentBlocks || !moduleLayout) return false;

  if (moduleLayout === ContentBlocksModuleLayout.ThreeCardBlock) {
    return (
      contentBlocks.every(
        (block) =>
          block.description.length <= THREE_CARD_BLOCK_DESCRIPTION_LIMIT &&
          block.description.length > 0 &&
          block.title.length > 0,
      ) && cardBlockImages.every((image) => !!image)
    );
  }

  if (moduleLayout === ContentBlocksModuleLayout.OneCardBlock) {
    if (brandId === BrandType.FREE) {
      return (
        contentBlocks.every(
          (block) =>
            block.description.length <= TEXT_BLOCK_DESCRIPTION_LIMIT &&
            block.description.length > 0 &&
            block.title.length > 0 &&
            ((block.selectedGame && block.selectedGame.value.length > 0) ||
              block.url),
        ) && cardBlockImages.every((image) => !!image)
      );
    }

    return (
      contentBlocks.every(
        (block) =>
          block.description.length <= TEXT_BLOCK_DESCRIPTION_LIMIT &&
          block.description.length > 0 &&
          block.title.length > 0 &&
          block.ctaForRegisteredUser &&
          block.ctaForRegisteredUser.length > 0 &&
          block.ctaForUnRegisteredUser &&
          block.ctaForUnRegisteredUser.length > 0 &&
          ((block.selectedGame && block.selectedGame.value.length > 0) ||
            block.url),
      ) && cardBlockImages.every((image) => !!image)
    );
  }

  return contentBlocks.every(
    (block) =>
      block.description.length <= TEXT_BLOCK_DESCRIPTION_LIMIT &&
      block.description.length > 0 &&
      block.title.length > 0,
  );
};

export const isOneCardBlockModuleLayout = (moduleType: ModuleLayout | null) =>
  ContentBlocksModuleLayout.OneCardBlock === moduleType;

export const isThreeCardBlockModuleTypeLayout = (
  moduleType: ModuleLayout | null,
) => ContentBlocksModuleLayout.ThreeCardBlock === moduleType;

// TODO: Temporary! Keep formatGameOptions util it will be moved to the directory related with SelectedGames widget
export const formatGameOptions = <
  T extends {
    selectedGames: ModuleGame[];
    manualSelectedGames: ModuleGame[];
  },
>(
  games: Game[],
  moduleData?: T | null,
): Map<string, GameOption> => {
  const gamesMap = new Map();
  const selectedGameIdsByTag = new Set(
    moduleData?.selectedGames.map((game) => game.id) ?? [],
  );
  const selectedGameIdsManually = new Set(
    moduleData?.manualSelectedGames.map((game) => game.id) ?? [],
  );

  games.forEach((game) => {
    const gameId = String(game.id);
    gamesMap.set(gameId, {
      ...game,
      name: game.displayName,
      addedByTag: moduleData ? selectedGameIdsByTag.has(gameId) : false,
      addedManually: moduleData ? selectedGameIdsManually.has(gameId) : false,
      disabled:
        moduleData &&
        selectedGameIdsByTag.has(gameId) &&
        !selectedGameIdsManually.has(gameId),
    });
  });

  return gamesMap;
};
